import Vue from 'vue';
import Vuex, { MutationTree, ActionTree, GetterTree } from 'vuex';

import { UserProfileState, Section } from '../models/store-viewmodel';
import { root } from '@/shared/store/index';
import { Config } from '../models/config-viewmodel';
import { Address, PhoneNumber, UserProfile, Ice } from '../models/user-profile-viewmodel';
import { Country } from '../models/country-viewmodel';
import { currentDate } from '@/shared/services/format-date-filter';

Vue.use(Vuex);

const state: UserProfileState = {
    loading: true,
    error: '',
    config: {
        language: '',
        labels: {},
        passwordUrl: '',
        disabilitiesUrl: '',
        studielinkUrl: ''
    },
    formSection: {
        phoneNumbers: new Section(),
        correspondence: new Section(),
        ice: new Section(),
        temporary: new Section()
    },
    formData: {
        phoneNumbers: [],
        correspondence: new Address('','','','','','','','',''),
        ice: [],
        temporary: new Address('','','','','','','','','')
    }
}

const mutations: MutationTree<UserProfileState> = {
    setConfig(state, config: Config) {
        state.config = config;
    },

    setLoading(state, loading: boolean) {
        state.loading = loading;
    },

    setError(state, error: string) {
        state.error = error;
    },

    setProfileData(state, data: UserProfile) {
        Vue.set(state, 'profileData', data);
    },

    setCountries(state, countries: Country[]) {
        state.countries = countries;
    },

    setFormSectionOpen(state, data: {type: string, status: boolean}) {
        state.formSection[data.type].open = data.status;
    },

    setFormSectionLoading(state, data: {type: string, status: boolean}) {
        state.formSection[data.type].loading = data.status;
    },

    setFormSectionSubmitted(state, data: {type: string, status: boolean}) {
        state.formSection[data.type].submitted = data.status;
    },

    setFormSectionSuccess(state, data: {type: string, status: boolean}) {
        state.formSection[data.type].success = data.status;
    },

    setFormSectionError(state, data: {type: string, status: boolean}) {
        state.formSection[data.type].error = data.status;
    },

    setFormSectionFieldErrors(state, data: {type: string, errors: []}) {
        state.formSection[data.type].fieldErrors = data.errors;
    },

    setFormDataPhoneNumbers(state, data: PhoneNumber[]) {
        state.formData.phoneNumbers = data;
    },

    setFormDataCorrespondence(state, data: Address) {
        state.formData.correspondence = data;
    },

    setFormDataTemporary(state, data: Address) {
        state.formData.temporary = data;
    },

    setFormDataIce(state, data: PhoneNumber[]) {
        state.formData.ice = data;
    }
}

const actions: ActionTree<UserProfileState, UserProfileState> = {
    formSectionOpen({commit, getters}, data: {type: string, status: boolean}) {
        if (data.type === 'phoneNumbers') {
            let phoneNumbersArr = JSON.parse(JSON.stringify(getters.phoneNumbers));
            commit('setFormDataPhoneNumbers', [phoneNumbersArr[0] || new PhoneNumber('', ''), phoneNumbersArr[1] || new PhoneNumber('', '')]);
        }
 
        if (data.type === 'ice') {
            let phoneNumbersArr = JSON.parse(JSON.stringify(getters.phoneNumbersIce));
            commit('setFormDataIce', [phoneNumbersArr[0] || new PhoneNumber('', '')]);
        }

        if (data.type === 'correspondence') {
            commit('setFormDataCorrespondence', JSON.parse(JSON.stringify(getters.correspondenceAddress)));
        }

        if (data.type === 'temporary') {
            commit('setFormDataTemporary', JSON.parse(JSON.stringify(getters.temporaryAddress || new Address('','','','','','','','',''))));
        }

        commit('setFormSectionOpen', {type: data.type, status: data.status})

        if (data.status === true) {
            commit('setFormSectionSubmitted', {type: data.type, status: false});
            commit('setFormSectionSuccess', {type: data.type, status: false});
            commit('setFormSectionError', {type: data.type, status: false});
        }
    }
}

const getters: GetterTree<UserProfileState, UserProfileState> = {
    config(state) {
        return state.config;
    },

    loading(state) {
        return state.loading;
    },

    error(state) {
        return state.error;
    },

    studies(state) {
        return [...new Set(state.profileData?.studentInfo.studies
            .map(study => study.studyName)
            .filter(studyName => studyName !== null))];
    },

    faculties(state) {
        return [...new Set(state.profileData?.studentInfo.studies
            .map(study => study.facultyName)
            .filter(facultyName => facultyName !== null))];
    },

    universityEmail(state) {
        return state.profileData?.studentInfo.emails
            .filter(email => email.isUniversityEmail)
            .map(email => email.emailAddress) || [];
    },

    personalEmail(state) {
        return state.profileData?.studentInfo.emails
            .filter(email => !email.isUniversityEmail)
            .map(email => email.emailAddress) || [];
    },

    address: (state) => (type: string) => {
        let date = currentDate();
        let found = state.profileData?.studentInfo.addresses
            .filter(address => address.type === type)
            .filter(address => address.validFrom <= date && address.validTo >= date)
            .sort((a, b) => (a.validTo < b.validTo) ? 1 : ((b.validTo < a.validTo) ? -1 : 0))
        return found && found.length > 0 ? found[found.length - 1] : null
    },

    residentialAddress(state, getters) {
        return getters.address('Residency') || {}
    },

    correspondenceAddress(state, getters) {
        return getters.address('Correspondence') || {}
    },

    temporaryAddress(state, getters) {
        return getters.address('Visiting')
    },

    ice(state) {
        return state.profileData?.studentInfo.ice;
    },

    phoneNumbers(state) {
        return state.profileData?.studentInfo.phoneNumbers;
    },

    phoneNumbersIce(state, getters) {
        return getters.ice ? getters.ice.phoneNumbers : [];
    },

    personalInfo(state) {
        return state.profileData?.personalInfo;
    },

    countryPhoneCode: (state) => (isoCode: string): string => {
        let found = state.countries?.filter(country => country.isoCode === isoCode)
        return found && found.length > 0 ? `+${found[0].countryCode}` : isoCode
    },

    countryPhoneName: (state) => (isoCode: string): string => {
        let found = state.countries?.filter(country => country.isoCode === isoCode)
        return found && found.length > 0 ? `${found[0].name}` : isoCode
    },

    sectionLoading: (state) => (type: string): boolean => {
        return state.formSection[type].loading;
    },
     
    sectionFormOpen: (state) => (type: string): boolean => {
        return state.formSection[type].open;
    },

    sectionSuccess: (state) => (type: string): boolean => {
        return state.formSection[type].success;
    },

    sectionError: (state) => (type: string): boolean => {
        return state.formSection[type].error;
    },

    sectionFieldErrors: (state) => (type: string): [] => {
        return state.formSection[type].fieldErrors;
    },

    sectionFormSubmitted: (state) => (type: string): boolean => {
        return state.formSection[type].submitted;
    },

    countryName: (state) => (isoCode: string): string => {
        let found = state.countries?.filter(country => country.isoCode === isoCode)
        return found && found.length > 0 ? found[0].name : isoCode
    },

    addressValid: (state) => (address: Address): boolean => {
        if (
            ((address.type === 'Correspondence' || address.type === 'Visiting') && address.street && address.city && address.country && address.houseNumber &&address.zipcode)
            || (address.type === 'Visiting' && !address.street && !address.city && !address.country && !address.houseNumber && !address.zipcode)) {
            return true;
        }

        return false
    },

    dataComparison: (state) => (dateOne: any, dataTwo: any): boolean => {
        return !(JSON.stringify(dateOne) === JSON.stringify(dataTwo));
    },

    correspondenceDataModified(state, getters): boolean {
        return getters.correspondenceAddress ?
            getters.dataComparison(getters.correspondenceAddress, state.formData.correspondence) :
            getters.dataComparison(new Address('','','','','','','','',''), state.formData.correspondence);
    },

    temporaryDataModified(state, getters): boolean {
        return getters.temporaryAddress ? 
            getters.dataComparison(getters.temporaryAddress, state.formData.temporary) :
            getters.dataComparison(new Address('','','','','','','','',''), state.formData.temporary);
    },

    phoneNumbersDataModified(state, getters): boolean {
        return getters.phoneNumbers ? 
            getters.dataComparison(getters.phoneNumbers, state.formData.phoneNumbers) :
            getters.dataComparison([new PhoneNumber('', ''), new PhoneNumber('', '')], state.formData.phoneNumbers);
    },

    iceDataModified(state, getters): boolean {
        return getters.ice ? 
            getters.dataComparison(getters.phoneNumbersIce, state.formData.ice) :
            getters.dataComparison([new PhoneNumber('', '')], state.formData.ice);
    },

    profileData(state) {
        return state.profileData;
    },

    countries(state) {
        return state.countries;
    },

    formData(state) {
        return state.formData;
    },

    formSection(state) {
        return state.formSection;
    }
}

export default new Vuex.Store({
    state,
    mutations,
    actions,
    getters,
    modules: {
        root
    }
})